/**
 * 65.28 (decision 161; the owner: "I think yes, but not sure how far we'll go with the suppressed status and player
 * impact"): suppression, with GameConfig.suppression. The bullets' step names the living enemies each round passed near
 * (core/bullets.ts stepBullets' `near`: within SUPPRESSION.radiusPeople of the body, before the round stopped); each is
 * marked `suppressed` for SUPPRESSION.seconds, renewed by every near round (never shortened — the capture rule's mark on a
 * hit may be longer), and the mark runs down by the tick; a fallen unit's mark goes. What it
 * does is its readers': a suppressed unit cannot capture (core/capturePoints.ts), a bot drops behind its cover and holds
 * its bound (ai/actionBot.ts), the player sees it (the HUD's edge). Pure.
 *
 * The grip (GameConfig.suppressionGrip, DECISIONS 163 (a)): `mark` as above (today's); `beat` — a near round ducks the unit
 * for SUPPRESSION.beat.seconds, then near rounds pass it by for beat.burstSeconds (its burst goes on), when the next can duck
 * it again; `weight` — each near round loads it (`suppressLoad`, leaking at weight.rounds ÷ weight.seconds a second), and
 * a load at weight.rounds marks it as the mark does. `nearRounds` counts the rounds (a unit passed by two this tick: two).
 */
import type { GameState, PlayerState } from '../types';
import { SUPPRESSION } from '../constants/action';

/** One tick (`dt` s) of suppression: every mark runs down, then the units named in `near` are marked afresh (by the grip). */
export function stepSuppression(state: GameState, dt: number, near: readonly number[], nearRounds: readonly { unitId: number }[] = []): GameState {
  const grip = state.config.suppressionGrip ?? 'mark';
  if (grip === 'beat') return stepBeat(state, dt, near);
  if (grip === 'weight') return stepWeight(state, dt, nearRounds);
  if (near.length === 0 && !state.players.some(p => p.suppressed)) return state;
  const fresh = new Set(near);
  return {
    ...state,
    players: state.players.map(p => {
      if (fresh.has(p.id) && p.alive) return { ...p, suppressed: Math.max(SUPPRESSION.seconds, (p.suppressed ?? 0) - dt) };   // (renewed, never shortened: a hit's longer mark stands)
      if (!p.suppressed) return p;
      const left = p.suppressed - dt;
      if (left > 0 && p.alive) return { ...p, suppressed: left };
      const { suppressed: _gone, ...rest } = p;
      return rest;
    }),
  };
}

/** A mark run down by `dt` (gone at nought, or with its unit). */
function runDown(p: PlayerState, dt: number): PlayerState {
  if (!p.suppressed) return p;
  const left = p.suppressed - dt;
  if (left > 0 && p.alive) return { ...p, suppressed: left };
  const { suppressed: _gone, ...rest } = p;
  return rest as PlayerState;
}

/** The grip `beat`: a near round ducks a unit for a beat unless it is in its rest (the beat it ducked, then its burst). */
function stepBeat(state: GameState, dt: number, near: readonly number[]): GameState {
  if (near.length === 0 && !state.players.some(p => p.suppressed || p.suppressRest)) return state;
  const fresh = new Set(near);
  const { seconds, burstSeconds } = SUPPRESSION.beat;
  return {
    ...state,
    players: state.players.map(p => {
      let q = runDown(p, dt);
      if (q.suppressRest) {
        const left = q.suppressRest - dt;
        if (left > 0 && q.alive) q = { ...q, suppressRest: left };
        else { const { suppressRest: _r, ...rest } = q; q = rest as PlayerState; }
      }
      if (fresh.has(p.id) && p.alive && !q.suppressRest) q = { ...q, suppressed: Math.max(seconds, q.suppressed ?? 0), suppressRest: seconds + burstSeconds };
      return q;
    }),
  };
}

/** The grip `weight`: near rounds load a unit, the load leaks; at weight.rounds the unit is marked (renewed while it holds). */
function stepWeight(state: GameState, dt: number, nearRounds: readonly { unitId: number }[]): GameState {
  if (nearRounds.length === 0 && !state.players.some(p => p.suppressed || p.suppressLoad)) return state;
  const rounds = new Map<number, number>();
  for (const r of nearRounds) rounds.set(r.unitId, (rounds.get(r.unitId) ?? 0) + 1);
  const { rounds: full, seconds: window } = SUPPRESSION.weight;
  const leak = (full / window) * dt;
  return {
    ...state,
    players: state.players.map(p => {
      let q = runDown(p, dt);
      const load = Math.max(0, (q.suppressLoad ?? 0) - leak) + (p.alive ? rounds.get(p.id) ?? 0 : 0);
      if (load > 1e-9 && q.alive) q = { ...q, suppressLoad: load };
      else if (q.suppressLoad !== undefined) { const { suppressLoad: _l, ...rest } = q; q = rest as PlayerState; }
      if (q.alive && load >= full) q = { ...q, suppressed: Math.max(SUPPRESSION.seconds, q.suppressed ?? 0) };
      return q;
    }),
  };
}
